import React, { ReactNode } from 'react';
import { Typography } from '@pwskills/rachnaui';

const headingClassName = 'font-inter !text-gray-850 pt-4 regular:pt-6 pb-3 regular:pb-4';
const contentClassName = 'flex flex-col space-y-2 regular:space-y-4 text-gray-850';
const listClassName = 'pl-5 list-disc space-y-0.5 regular:space-y-1 [&>li>p]:!text-gray-850';

type PolicyTableProps = {
  headers: string[];
  rows: ReactNode[][];
};

const getNodeKey = (node: ReactNode) => {
  if (React.isValidElement(node)) {
    return String(node.key ?? node.props.children);
  }

  return String(node);
};

const PolicyHeading = ({ children }: { children: ReactNode }) => (
  <Typography component="h4" variant="bold" className={headingClassName}>
    {children}
  </Typography>
);

const PolicySubHeading = ({ children }: { children: ReactNode }) => (
  <Typography component="h5" variant="bold" className={headingClassName}>
    {children}
  </Typography>
);

const PolicyTable = ({ headers, rows }: PolicyTableProps) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[640px] border-collapse border border-gray-500 text-left text-sm regular:text-base">
      <thead className="bg-blue-100">
        <tr>
          {headers.map((header) => (
            <th key={header} className="border border-gray-500 p-2 regular:p-3 font-semibold">
              {header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const rowKey = getNodeKey(row[0]);

          return (
            <tr key={rowKey}>
              {headers.map((header, cellIndex) => (
                <td key={`${rowKey}-${header}`} className="border border-gray-500 p-2 regular:p-3">
                  {row[cellIndex]}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const RefundPolicy = () => (
  <section>
    <Typography component="h3" variant="bold" className={headingClassName}>
      Refund Policy
    </Typography>

    <PolicyHeading>1. Purpose</PolicyHeading>
    <div className={contentClassName}>
      <Typography component="body-regular" variant="regular">
        At PW Skills, we are committed to providing learners with a transparent and clearly defined
        refund process. This Refund Policy outlines the refund eligibility, applicable
        non-refundable registration fee, payment-mode treatment, refund process, retention process,
        and timelines for learners enrolled in PW Skills programmes.
      </Typography>
      <Typography component="body-regular" variant="regular">
        By enrolling in a PW Skills programme, the learner acknowledges and agrees to the terms of
        this Refund Policy.
      </Typography>
    </div>

    <PolicyHeading>2. Course Categories</PolicyHeading>
    <div className={contentClassName}>
      <Typography component="body-regular" variant="regular">
        PW Skills programmes are offered under different course categories with different delivery
        formats and pricing.
      </Typography>
      <PolicyTable
        headers={['Course Type', 'Positioning', 'Delivery Mode']}
        rows={[
          [<strong key="basic">Basic</strong>, 'Self-Paced', 'Recorded'],
          [
            <strong key="premium">Premium</strong>,
            'Live + Hybrid',
            'Live weekend sessions + Recorded',
          ],
          [
            <strong key="pro">Pro</strong>,
            'Live + Hybrid + Placement Support',
            'Live weekend sessions + Recorded',
          ],
        ]}
      />
      <Typography component="body-regular" variant="regular">
        The applicable refund terms will depend on the course category purchased by the learner.
      </Typography>
    </div>

    <PolicyHeading>3. Refund Eligibility</PolicyHeading>
    <PolicySubHeading>3.1 Basic Courses</PolicySubHeading>
    <div className={contentClassName}>
      <Typography component="body-regular" variant="regular">
        <strong>Basic courses are non-refundable.</strong>
      </Typography>
      <Typography component="body-regular" variant="regular">
        Once a learner completes the purchase/enrolment for a Basic course, no refund will be
        applicable, irrespective of the payment mode or the time elapsed from the date of purchase.
      </Typography>
      <PolicyTable
        headers={['Course Type', 'Refund Eligibility']}
        rows={[[<strong key="basic">Basic</strong>, <strong key="refund">No refund</strong>]]}
      />
      <Typography component="body-regular" variant="regular">
        This applies to purchases made through full payment, part payment, EMI, loan, or FlexiPay,
        wherever applicable.
      </Typography>
    </div>

    <PolicySubHeading>3.2 Premium &amp; Pro Courses</PolicySubHeading>
    <div className={contentClassName}>
      <Typography component="body-regular" variant="regular">
        Learners enrolled in a <strong>Premium &amp; Pro course</strong> may request a refund within{' '}
        <strong>30 calendar days from the date of purchase/enrolment</strong>.
      </Typography>
      <Typography component="body-regular" variant="regular">
        A mandatory <strong>₹10,000 registration fee</strong> will be applicable at the time of
        enrolment. This registration fee is <strong>non-refundable</strong> and will not be included
        in the refundable amount.
      </Typography>
      <Typography component="body-regular" variant="regular">
        For Banking &amp; Finance courses, the applicable non-refundable registration fee will be{' '}
        <strong>₹5,000</strong>.
      </Typography>
      <Typography component="body-regular" variant="regular">
        The remaining eligible course fee may be refunded if the learner raises the request within
        the applicable 30-day refund window, subject to the refund process and applicable
        payment-mode terms.
      </Typography>
    </div>

    <PolicyHeading>4. Refund Eligibility Timeline</PolicyHeading>
    <div className={contentClassName}>
      <Typography component="body-regular" variant="regular">
        For Premium and Pro courses, the refund window will be strictly limited to{' '}
        <strong>30 calendar days from the date of purchase/enrolment</strong>.
      </Typography>
      <PolicyTable
        headers={['Parameter', 'Basic', 'Premium', 'Pro']}
        rows={[
          [
            'Refund window',
            <strong key="basic">No refund</strong>,
            <strong key="premium">30 calendar days</strong>,
            <strong key="pro">30 calendar days</strong>,
          ],
          ['Window starts from', 'Date of purchase', 'Date of purchase', 'Date of purchase'],
          [
            'Mandatory registration fee (All Courses except Banking & Finance)',
            'Not applicable',
            <strong key="premium">₹10,000 (non-refundable)</strong>,
            <strong key="pro">₹10,000 (non-refundable)</strong>,
          ],
          [
            'Banking and Finance Course',
            'Not applicable',
            <strong key="premium-banking">₹5,000 (non-refundable)</strong>,
            <strong key="pro-banking">₹5,000 (non-refundable)</strong>,
          ],
          [
            'Refund after 30 days',
            <strong key="basic">Not applicable</strong>,
            <strong key="premium">No refund</strong>,
            <strong key="pro">No refund</strong>,
          ],
          [
            'Course usage condition',
            'Not applicable',
            'Access will be revoked',
            'Access will be revoked',
          ],
        ]}
      />
      <Typography component="body-regular" variant="regular">
        <strong>Day 1</strong> will be considered the date on which the learner completes the
        purchase/enrolment.
      </Typography>
      <Typography component="body-regular" variant="regular">
        The 30-calendar-day period will be calculated from the purchase/enrollment date.
      </Typography>
    </div>

    <PolicyHeading>5. Payment Modes</PolicyHeading>
    <Typography component="body-regular" variant="regular">
      The refund policy will apply consistently based on the course category and applicable fixed
      deduction.
    </Typography>

    <PolicySubHeading>5.1 Full Payment</PolicySubHeading>
    <div className={contentClassName}>
      <Typography component="body-regular" variant="regular">
        For eligible Premium and Pro courses purchased through full payment:
      </Typography>
      <ul className={listClassName}>
        <li>
          <Typography component="body-regular" variant="regular">
            <strong>Premium/Pro:</strong> The applicable ₹10,000 registration fee will be deducted
            and the remaining eligible amount will be refunded.
          </Typography>
        </li>
        <li>
          <Typography component="body-regular" variant="regular">
            <strong>Banking &amp; Finance:</strong> The applicable ₹5,000 registration fee will be
            deducted and the remaining eligible amount will be refunded.
          </Typography>
        </li>
      </ul>
    </div>

    <PolicySubHeading>5.2 Part Payment / Down Payment</PolicySubHeading>
    <div className={contentClassName}>
      <Typography component="body-regular" variant="regular">
        For All Premium and Pro enrolments, the applicable registration fee should be structured as
        the <strong>mandatory initial down payment</strong>, wherever the payment arrangement
        permits.
      </Typography>
      <Typography component="body-regular" variant="regular">
        <strong>Premium and Pro (Except Banking &amp; Finance Courses)</strong>
      </Typography>
      <ul className={listClassName}>
        <li>
          <Typography component="body-regular" variant="regular">
            <strong>₹10,000 mandatory registration fee/down payment</strong>
          </Typography>
        </li>
        <li>
          <Typography component="body-regular" variant="regular">
            Remaining course fee through the applicable payment/financing arrangement
          </Typography>
        </li>
      </ul>
      <Typography component="body-regular" variant="regular">
        <strong>For Banking &amp; Finance courses:</strong>
      </Typography>
      <ul className={listClassName}>
        <li>
          <Typography component="body-regular" variant="regular">
            <strong>₹5,000 mandatory registration fee/down payment</strong>
          </Typography>
        </li>
        <li>
          <Typography component="body-regular" variant="regular">
            Remaining course fee through the applicable payment/financing arrangement
          </Typography>
        </li>
      </ul>
      <Typography component="body-regular" variant="regular">
        The applicable registration fee will remain non-refundable if the learner requests a refund
        within the eligible 30-day period.
      </Typography>
    </div>

    <PolicyHeading>6. EMI / Loan / FlexiPay</PolicyHeading>
    <div className={contentClassName}>
      <Typography component="body-regular" variant="regular">
        For Premium and Pro courses enrolled through EMI, loan, or FlexiPay arrangements, the refund
        eligibility and applicable non-refundable registration fee will remain the same as stated in
        this policy.
      </Typography>
      <Typography component="body-regular" variant="regular">
        <strong>Premium and Pro</strong>
      </Typography>
      <ul className={listClassName}>
        <li>
          <Typography component="body-regular" variant="regular">
            Applicable registration fee for all courses: <strong>₹10,000</strong>
          </Typography>
        </li>
        <li>
          <Typography component="body-regular" variant="regular">
            Banking &amp; Finance courses: <strong>₹5,000</strong>
          </Typography>
        </li>
        <li>
          <Typography component="body-regular" variant="regular">
            Refund requests must be raised within 30 calendar days.
          </Typography>
        </li>
        <li>
          <Typography component="body-regular" variant="regular">
            Wherever operationally feasible, the applicable registration fee should be collected as
            the mandatory down payment and the remaining amount financed.
          </Typography>
        </li>
      </ul>
      <Typography component="body-regular" variant="regular">
        For eligible EMI/loan/FlexiPay refunds, PW Skills will initiate the applicable
        cancellation/refund process with the relevant financing partner.
      </Typography>
      <Typography component="body-regular" variant="regular">
        The actual closure or cancellation of the financing arrangement will be subject to the
        financing partner&apos;s applicable operational process and timelines.
      </Typography>
    </div>

    <PolicyHeading>7. No Refund After 30 Days</PolicyHeading>
    <div className={contentClassName}>
      <Typography component="body-regular" variant="regular">
        <strong>
          Refund requests raised after completion of 30 calendar days from the date of
          purchase/enrollment will not be eligible for a refund.
        </strong>
      </Typography>
      <Typography component="body-regular" variant="regular">
        This condition will be applied <strong>strictly</strong>.
      </Typography>
      <Typography component="body-regular" variant="regular">
        The completion of the 30-day period will result in the learner becoming ineligible for a
        refund, irrespective of:
      </Typography>
      <ul className={listClassName}>
        {[
          'Course usage or non-usage',
          'Attendance or non-attendance',
          'Access or non-access of course content',
          'Personal circumstances',
          'Change of plans',
          'Change of career or employment',
          'Failure to attend live sessions',
          'Failure to complete the programme',
        ].map((item) => (
          <li key={item}>
            <Typography component="body-regular" variant="regular">
              {item}
            </Typography>
          </li>
        ))}
      </ul>
      <Typography component="body-regular" variant="regular">
        Any exception, if considered, will require specific approval from the authorised management
        team and will not constitute a general learner entitlement.
      </Typography>
    </div>

    <PolicyHeading>8. Refund Request Process</PolicyHeading>
    <div className={contentClassName}>
      <Typography component="body-regular" variant="regular">
        Eligible refund requests must be raised through official PW Skills support channels:
      </Typography>
      <PolicyTable
        headers={['Channel', 'Process']}
        rows={[
          [
            <strong key="email">Email Support</strong>,
            <>
              Send the refund request to the official PW Skills Support email ID{' '}
              <strong>support@pwskills.com</strong>
            </>,
          ],
          [
            <strong key="lms">LMS Support</strong>,
            <>
              LMS → <strong>Contact Us</strong> → Raise a support request
            </>,
          ],
        ]}
      />
      <Typography component="body-regular" variant="regular">
        The refund request must be raised within the applicable{' '}
        <strong>30-calendar-day refund window</strong> for Premium and Pro courses.
      </Typography>
      <Typography component="body-regular" variant="regular">
        Learners should retain their support ticket/reference number for future communication
        regarding their refund request.
      </Typography>
    </div>

    <PolicyHeading>9. Refund Request TAT and Retention Process</PolicyHeading>
    <div className={contentClassName}>
      <Typography component="body-regular" variant="regular">
        All eligible refund requests will follow the below process to ensure that the learner&apos;s
        concern is reviewed and appropriate retention efforts are made before the refund is
        processed.
      </Typography>
      <PolicyTable
        headers={['Timeline', 'Process']}
        rows={[
          [
            <strong key="day-1">Day 1</strong>,
            'Learner raises the refund request through the official support channel.',
          ],
          [
            <strong key="day-1-2">Day 1–2</strong>,
            'SST reviews the request and makes the first-level retention effort with the learner.',
          ],
          [
            <strong key="day-2-4">Day 2–4</strong>,
            <>
              If the learner is not retained, the case is escalated to the relevant{' '}
              <strong>Category Head</strong> for further review and retention efforts.
            </>,
          ],
          [
            <strong key="day-5">Day 5</strong>,
            'If the learner is still not retained, the refund process will be initiated as per the applicable payment mode: Full Payment, Loan, EMI/FlexiPay, or other applicable payment arrangement.',
          ],
        ]}
      />
      <Typography component="body-regular" variant="regular">
        Once the refund is approved for processing, a{' '}
        <strong>confirmation email will be sent to the learner</strong> confirming that the refund
        has been initiated.
      </Typography>
      <Typography component="body-regular" variant="regular">
        The above TAT applies to eligible refund requests raised within the applicable refund
        window. The retention process does not extend or reset the 30-day refund eligibility period.
      </Typography>
    </div>

    <PolicyHeading>10. Refund Processing Timeline</PolicyHeading>
    <div className={contentClassName}>
      <Typography component="body-regular" variant="regular">
        Once the refund is initiated on Day 5, the refund will generally be credited within{' '}
        <strong>7–10 working days from the date of processing</strong>.
      </Typography>
      <PolicyTable
        headers={['Stage', 'Indicative Timeline']}
        rows={[
          ['Refund request', 'Within 30 calendar days'],
          ['SST retention effort', <strong key="sst">Day 1–2</strong>],
          ['Category Head retention effort', <strong key="category-head">Day 2–4</strong>],
          ['Refund initiation', <strong key="refund-initiation">Day 5, if not retained</strong>],
          [
            'Refund credit after processing',
            <strong key="refund-credit">7–10 working days</strong>,
          ],
        ]}
      />
      <Typography component="body-regular" variant="regular">
        The actual credit timeline may vary depending on the payment method, banking institution,
        payment gateway, or financing partner.
      </Typography>
      <Typography component="body-regular" variant="bold">
        <strong>Academic Issue Retention Support</strong>
      </Typography>
      <Typography component="body-regular" variant="regular">
        Where a refund request is raised due to an academic or learning-related concern, an
        additional retention effort may be made before refund initiation:
      </Typography>
      <ul className={listClassName}>
        <li>
          <Typography component="body-regular" variant="regular">
            <strong>Online Courses:</strong> The Assignment Evaluation Team may make one attempt to
            retain the learner by assisting the learner over a call and addressing the academic
            concern.
          </Typography>
        </li>
        <li>
          <Typography component="body-regular" variant="regular">
            <strong>Offline Courses:</strong> Assistant Faculty members may assist the learner and
            make an effort to resolve the academic concern and retain the learner.
          </Typography>
        </li>
      </ul>
      <Typography component="body-regular" variant="regular">
        This academic support is part of the retention process and does not extend or reset the
        30-day refund eligibility period.
      </Typography>
    </div>

    <PolicyHeading>11. LMS Refund Request Section</PolicyHeading>
    <div className={contentClassName}>
      <Typography component="body-regular" variant="regular">
        A dedicated <strong>Refund Request</strong> section will be created on the PW Skills LMS to
        provide learners with a clear and standardised refund request journey.
      </Typography>
      <Typography component="body-regular" variant="regular">
        The LMS section will display:
      </Typography>
      <ul className={listClassName}>
        <li>
          <Typography component="body-regular" variant="regular">
            The applicable <strong>30-day refund policy</strong> for eligible Premium and Pro
            courses.
          </Typography>
        </li>
        <li>
          <Typography component="body-regular" variant="regular">
            The applicable course-wise refund deduction.
          </Typography>
        </li>
        <li>
          <Typography component="body-regular" variant="regular">
            The <strong>Raise a Ticket</strong> option for submitting a refund request.
          </Typography>
        </li>
        <li>
          <Typography component="body-regular" variant="regular">
            Relevant information required to initiate and process the refund request.
          </Typography>
        </li>
      </ul>
      <Typography component="body-regular" variant="regular">
        The LMS refund request option will serve as an official channel for raising refund requests.
        The request date recorded through the LMS/support system will be considered for determining
        whether the learner has raised the request within the applicable refund window.
      </Typography>
    </div>

    <PolicyHeading>12. Course Cancellation or Postponement by PW Skills</PolicyHeading>
    <div className={contentClassName}>
      <Typography component="body-regular" variant="regular">
        If a programme is cancelled by PW Skills, learners will be eligible to request a refund as
        per the applicable terms communicated by PW Skills.
      </Typography>
      <Typography component="body-regular" variant="regular">
        If a programme is postponed by PW Skills before its scheduled commencement, PW Skills may
        provide an updated refund window based on the revised programme commencement date, wherever
        applicable.
      </Typography>
      <Typography component="body-regular" variant="regular">
        The applicable terms will be communicated to affected learners through official channels.
      </Typography>
    </div>

    <PolicyHeading>13. Refund Calculation</PolicyHeading>
    <div className={contentClassName}>
      <Typography component="body-regular" variant="regular">
        The refund amount will be calculated based on the applicable course category and
        non-refundable registration fee.
      </Typography>
      <Typography component="body-regular" variant="regular">
        <strong>Premium and Pro Courses</strong>
      </Typography>
      <Typography component="body-regular" variant="regular">
        <strong>Eligible Refund =</strong> Applicable Eligible Amount Paid − ₹10,000 non-refundable
        registration fee
      </Typography>
      <Typography component="body-regular" variant="regular">
        <strong>Banking &amp; Finance Courses</strong>
      </Typography>
      <Typography component="body-regular" variant="regular">
        <strong>Eligible Refund =</strong> Applicable Eligible Amount Paid − ₹5,000 non-refundable
        registration fee
      </Typography>
      <Typography component="body-regular" variant="regular">
        <strong>Basic Courses</strong>
      </Typography>
      <Typography component="body-regular" variant="regular">
        <strong>No refund applicable.</strong>
      </Typography>
    </div>

    <PolicyHeading>14. Policy Acceptance</PolicyHeading>
    <div className={contentClassName}>
      <Typography component="body-regular" variant="regular">
        <strong>Counsellor Responsibility</strong>
      </Typography>
      <Typography component="body-regular" variant="regular">
        Counsellors are{' '}
        <strong>
          mandatorily required to inform learners about the applicable refund policy and the
          30-calendar-day refund period before completing the enrolment
        </strong>
        . The counsellor must clearly communicate the applicable refund eligibility, the
        non-refundable ₹10,000 registration fee for Premium and Pro courses, and that{' '}
        <strong>no refund will be applicable after completion of 30 calendar days</strong>.
      </Typography>
      <Typography component="body-regular" variant="regular">
        The applicable refund terms should be clearly communicated to the learner before
        payment/enrollment so that the learner is aware of the policy and applicable conditions
        before making the purchase.
      </Typography>
    </div>

    <PolicyHeading>15. Summary of Refund Policy</PolicyHeading>
    <PolicyTable
      headers={['Policy Component', 'Basic', 'Premium', 'Pro']}
      rows={[
        [
          <strong key="refund-eligibility">Refund Eligibility</strong>,
          'No refund',
          'Within 30 days',
          'Within 30 days',
        ],
        [
          <strong key="registration-fee">
            Registration Fee (All courses except Banking & Finance)
          </strong>,
          'Not applicable',
          <strong key="premium">₹10,000 non-refundable</strong>,
          <strong key="pro">₹10,000 non-refundable</strong>,
        ],
        [
          <strong key="banking-registration-fee">Banking & Finance Registration Fee</strong>,
          'No Refund',
          <strong key="premium-banking">₹5,000 non-refundable</strong>,
          <strong key="pro-banking">₹5,000 non-refundable</strong>,
        ],
        [
          <strong key="full-payment">Full Payment</strong>,
          'No refund',
          'Refund eligible balance after ₹10,000 registration fee',
          'Refund eligible balance after ₹10,000 registration fee',
        ],
        [
          <strong key="part-payment">Part Payment</strong>,
          'No refund',
          '₹10,000 mandatory registration fee/DP',
          '₹10,000 mandatory registration fee/DP',
        ],
        [
          <strong key="emi-loan">EMI / Loan</strong>,
          'No refund',
          '₹10,000 applicable non-refundable registration fee',
          '₹10,000 applicable non-refundable registration fee',
        ],
        [
          <strong key="flexipay">FlexiPay</strong>,
          'No refund',
          '₹10,000 applicable deduction',
          '₹10,000 applicable deduction',
        ],
        [
          <strong key="refund-after-30-days">Refund After 30 Days</strong>,
          'Not applicable',
          <strong key="premium">No refund — strict</strong>,
          <strong key="pro">No refund — strict</strong>,
        ],
        [
          <strong key="refund-initiation">Refund Initiation</strong>,
          'Not applicable',
          <strong key="premium">Day 5, if not retained</strong>,
          <strong key="pro">Day 5, if not retained</strong>,
        ],
        [
          <strong key="refund-credit">Refund Credit After Processing</strong>,
          'Not applicable',
          <strong key="premium">7–10 working days</strong>,
          <strong key="pro">7–10 working days</strong>,
        ],
        [
          <strong key="refund-request">Refund Request</strong>,
          'Not applicable',
          'Support Email / LMS',
          'Support Email / LMS',
        ],
      ]}
    />

    <PolicyHeading>16. Policy Amendment</PolicyHeading>
    <div className={contentClassName}>
      <Typography component="body-regular" variant="regular">
        PW Skills reserves the right to modify, update, or revise this Refund Policy from time to
        time. Any applicable changes will be communicated through appropriate official channels and
        will apply to enrolments made after the effective date of the revised policy.
      </Typography>
    </div>
  </section>
);

export default RefundPolicy;
